/*
  Kiwi Ears · guided product finder (Rene 2026-08-30).

  A port of the kiwi_ears_product_finder prototype's guided mode into the
  theme. What was kept, what moved, and what was cut — and why:

  KEPT VERBATIM — the scoring model from the prototype's AGENT_HANDOFF:
    product type 30 · accessory job 30 · each connection 11 · each scenario 18
    · primary sound 26 / secondary 18 · budget up to 20 (soft wall: under
    budget still lands 14, up to 18% over lands 9) · each priority 12 ·
    availability up to 4. The displayed number is a FINDER-ALIGNMENT score,
    never a quality rating, confidence-adjusted by quiz completeness and
    clamped to 52–98 so a half-answered quiz cannot print false precision.
    Also kept: the step definitions per category, the accessory path
    (job + connections replace scenarios + sound), the priority→tag expansion
    table, skippability of everything but the category, and the featured-order
    tiebreak (fit → stock → featured → price).

  MOVED TO THE SERVER — every product card. The prototype built cards from a
  static JSON snapshot; here Liquid renders the house card (pk-pair-card) for
  the whole candidate pool at page load with live price/stock/rating, and
  this file only ranks and MOVES those nodes. No card markup exists in this
  file, per the one-card rule and the client-rendered-cards rule in CLAUDE.md.

  CUT — the prototype's browse-all mode (the page already has fnd-filter with
  live facets right below; the quiz links to it), quick-view dialogs (cards
  link to their PDPs), and localStorage persistence (state lives in the URL
  instead — ?fq=…&fqs=… — which survives reload AND can be pasted to a
  friend, the house rule).

  Reduced motion: no GSAP dependency here at all — transitions are CSS and
  gated there; focus management is the only motion-adjacent behaviour.
*/
(function () {
  'use strict';
  if (window.__kiwiFndQuiz) return;
  window.__kiwiFndQuiz = true;

  var root = document.getElementById('fnd-quiz');
  if (!root) return;

  var $ = function (sel, r) { return (r || root).querySelector(sel); };
  var $$ = function (sel, r) { return [].slice.call((r || root).querySelectorAll(sel)); };
  var clamp = function (v, a, b) { return Math.min(b, Math.max(a, v)); };

  /* ------------------------------------------------------------ catalogue */
  var META;
  try { META = JSON.parse($('[data-fndq-meta]').textContent); }
  catch (e) { return; }   /* no metadata, no quiz — the browser below still works */

  var PRODUCTS = [];
  $$('[data-fndq-item]').forEach(function (node) {
    var m = META.products[node.dataset.handle];
    if (!m) return;   /* unclassified product: honest exclusion */
    PRODUCTS.push({
      handle: node.dataset.handle,
      title: node.dataset.title,
      price: parseFloat(node.dataset.price),
      avail: node.dataset.avail,
      url: node.dataset.url,
      img: node.dataset.img,
      /* buy-box facts for the leading-match panel — Liquid-only data (brand
         line, kiwi-name, metafield blurb, money-formatted prices, rating),
         emitted per item because JS cannot reach any of it */
      vid: node.dataset.vid,
      name: node.dataset.name || node.dataset.title,
      vendor: node.dataset.vendor,
      blurb: node.dataset.sub,
      priceFmt: node.dataset.priceFmt,
      cmpFmt: node.dataset.cmpFmt,
      varies: node.dataset.varies === '1',
      rating: node.dataset.rating,
      rcount: node.dataset.rcount,
      cat: m.cat, sub: m.sub, sounds: m.sounds, scen: m.scen, prio: m.prio,
      need: m.need, conn: m.conn, strength: m.strength, tradeoff: m.tradeoff,
      tags: m.tags, node: node
    });
  });
  if (!PRODUCTS.length) return;
  var FEATURED = META.featured || [];

  /* ------------------------------------------------------------- content */
  var LABELS = {
    category: { iem: 'IEMs', headphone: 'Wired headphones', wireless: 'Wireless audio', accessory: 'Accessories', audio: 'Help me decide' },
    scenario: { everyday: 'Everyday music', gaming: 'Gaming', studio: 'Studio monitoring', critical: 'Critical listening', commute: 'Commute & travel', home: 'Home listening', sport: 'Sport & awareness', calls: 'Work & calls', travel: 'Travel' },
    sound: { balanced: 'Balanced', powerful: 'Powerful', vocal: 'Vocal', warm: 'Warm', spacious: 'Spacious' },
    budget: { 'under-25': 'Under $25', 'under-50': 'Under $50', 'under-100': 'Under $100', 'under-200': 'Under $200', 'under-350': 'Under $350', '350-plus': '$350+', flexible: 'Flexible budget' },
    need: { dac: 'DAC or adapter', cable: 'Replacement cable', tips: 'Ear tips', case: 'Protective case', gaming: 'Gaming mic cable' },
    connection: { 'usb-c': 'USB-C', '3.5mm': '3.5 mm', '4.4mm': '4.4 mm balanced', '2-pin': '0.78 mm 2-pin', headphone: 'Headphone connector', microphone: 'Microphone support', iem: 'IEM compatible' },
    priority: { bass: 'Bass impact', vocals: 'Natural vocals', detail: 'Fine detail', soundstage: 'Soundstage', imaging: 'Precise imaging', isolation: 'Isolation', comfort: 'Long-session comfort', value: 'Best value', tuning: 'Tuning flexibility', microphone: 'Microphone', battery: 'Battery life', anc: 'Noise cancellation', awareness: 'Environmental awareness', portability: 'Portability', power: 'More output power', balancedOutput: 'Balanced output', eq: 'EQ control', protection: 'Protection', seal: 'Better seal', openness: 'More openness', modular: 'Modular connections', gaming: 'Gaming features' }
  };

  var PRIORITY_MATCH = {
    bass: ['bass','sub-bass','tactile','dynamics','fun','warmth','immersion'],
    vocals: ['vocals','midrange','natural-tone','warmth'],
    detail: ['detail','resolution','speed','air','separation','professional'],
    soundstage: ['soundstage','air','openness','immersion'],
    imaging: ['imaging','separation','monitoring','professional'],
    isolation: ['isolation','seal','privacy'],
    comfort: ['comfort','secure-fit','portability','replaceable-pads'],
    value: ['value'],
    tuning: ['tuning-flexibility','eq','sound-modes','modular'],
    microphone: ['microphone','controls','calls'],
    battery: ['battery'], anc: ['anc'],
    awareness: ['awareness','open-wear'],
    portability: ['portability','foldable','travel'],
    power: ['power','headphones'],
    balancedOutput: ['balanced-output'],
    eq: ['eq','dsp','sound-modes'],
    protection: ['protection','organization','durability'],
    seal: ['seal','isolation','bass','hygiene'],
    openness: ['openness','treble','clarity'],
    modular: ['modular','balanced-output','usb-c'],
    gaming: ['gaming','microphone','dsp','controls','low-latency']
  };

  var BUDGETS = { 'under-25': [0,25], 'under-50': [0,50], 'under-100': [0,100], 'under-200': [0,200], 'under-350': [0,350], '350-plus': [350,Infinity], flexible: [0,Infinity] };

  /* mini tonal shapes for the sound step — same illustrative-only discipline
     as sn-hero/sn-tonal, drawn in this card's own 220×58 space */
  var SOUND_PATHS = {
    balanced: 'M12 37 C42 36 64 34 88 33 C112 31 131 24 149 20 C168 17 187 24 208 30',
    powerful: 'M12 16 C40 18 57 25 75 34 C95 43 119 43 141 37 C164 31 184 33 208 39',
    vocal: 'M12 39 C44 39 67 34 87 25 C105 17 126 17 145 23 C165 30 184 36 208 38',
    warm: 'M12 23 C44 18 68 19 91 25 C116 31 137 36 158 37 C178 37 193 39 208 42',
    spacious: 'M12 40 C46 39 76 37 103 34 C132 31 153 25 173 17 C188 11 198 8 208 7'
  };

  function opt(v, label, desc) { return { value: v, label: label, desc: desc }; }

  function scenarioOptions(cat) {
    if (cat === 'wireless') return [
      opt('commute','Commute & travel','Block noise or stay aware while moving.'),
      opt('calls','Work & calls','Clear communication and all-day convenience.'),
      opt('sport','Sport & awareness','Secure movement with environmental awareness.'),
      opt('gaming','Gaming','Low-latency play and communication.'),
      opt('everyday','Everyday music','A versatile choice for the whole day.')];
    if (cat === 'headphone') return [
      opt('home','Home listening','A full-size setup for focused sessions.'),
      opt('studio','Studio monitoring','Tracking, editing, and portable reference.'),
      opt('critical','Critical listening','Resolution, timbre, and spatial insight.'),
      opt('gaming','Gaming','Immersion, imaging, and long-session comfort.'),
      opt('everyday','Everyday music','A versatile wired headphone for regular use.')];
    return [
      opt('everyday','Everyday music','Versatile listening across genres and devices.'),
      opt('gaming','Gaming','Imaging, separation, and long-session comfort.'),
      opt('studio','Studio monitoring','Tonal consistency, isolation, and mix information.'),
      opt('critical','Critical listening','Detail, timbre, layering, and extension.'),
      opt('commute','Commute & travel','Portable sound with comfort and useful isolation.'),
      opt('home','Home listening','Immersive, distraction-free listening at home.')];
  }

  var PRIORITY_GROUPS = {
    iem: ['bass','vocals','detail','soundstage','imaging','isolation','comfort','value','tuning','microphone'],
    audio: ['bass','vocals','detail','soundstage','imaging','comfort','value','portability','microphone'],
    headphone: ['soundstage','detail','vocals','comfort','isolation','value','portability','bass','imaging'],
    wireless: ['anc','battery','microphone','awareness','comfort','value','portability','bass','gaming'],
    accessory: ['power','balancedOutput','microphone','eq','portability','protection','seal','openness','modular','gaming']
  };
  var PRIORITY_DESC = {
    bass: 'More depth, punch, and physical weight.', vocals: 'Voices with body, clarity, and presence.',
    detail: 'Hear subtle textures and low-level information.', soundstage: 'More perceived width, depth, and openness.',
    imaging: 'More precise positional placement.', isolation: 'Reduce outside sound and maintain a seal.',
    comfort: 'Better suited to extended listening.', value: 'Maximize performance for the budget.',
    tuning: 'Switches, filters, EQ, or modular choices.', microphone: 'Calls, chat, or gaming communication.',
    battery: 'Longer listening between charges.', anc: 'Reduce environmental noise electronically.',
    awareness: 'Stay connected to the environment.', portability: 'Compact, simple, and easy to carry.',
    power: 'More output for demanding headphones.', balancedOutput: 'Use a 4.4 mm balanced connection.',
    eq: 'Adjust the sound through EQ or DSP.', protection: 'Protect and organize the setup.',
    seal: 'Improve bass support and passive isolation.', openness: 'Preserve air and upper-frequency clarity.',
    modular: 'Change source connections without replacing the cable.', gaming: 'Mic, controls, DSP, or low-latency features.'
  };

  function steps() {
    var cat = state.category;
    var categoryStep = {
      id: 'category', kicker: 'Question 1', title: 'What kind of product are you looking for?',
      help: 'Choose a category, or let the finder compare audio products across the range.',
      type: 'single', required: true,
      options: [
        opt('iem','In-ear monitors','Compact high-fidelity listening and strong passive isolation.'),
        opt('headphone','Wired headphones','Open- or closed-back full-size listening.'),
        opt('wireless','Wireless audio','ANC headphones and open-wear earphones.'),
        opt('accessory','Accessories','DACs, cables, ear tips, and protection.'),
        opt('audio','Help me decide','Compare IEMs, wired headphones, and wireless audio.')]
    };
    if (cat === 'accessory') return [categoryStep,
      { id: 'need', kicker: 'Question 2', title: 'What do you need the accessory to do?',
        help: 'Pick the closest job. You can refine the connector on the next step.', type: 'single',
        options: [
          opt('dac','Connect or power audio','USB-C adapters and portable DAC/amps.'),
          opt('cable','Replace or upgrade a cable','Modular IEM and headphone cables.'),
          opt('tips','Improve fit or sound','Seal-focused and wide-bore silicone tips.'),
          opt('case','Protect the setup','Structured storage for IEMs and accessories.'),
          opt('gaming','Add a gaming microphone','USB-C cable with boom mic, DSP, and controls.')] },
      { id: 'connections', kicker: 'Question 3', title: 'Which connections matter?',
        help: 'Choose up to two. Skip this when you are unsure about the connector.', type: 'multi', max: 2,
        options: [
          opt('usb-c','USB-C','Phones, tablets, laptops, and portable devices.'),
          opt('3.5mm','3.5 mm','Standard single-ended audio connection.'),
          opt('4.4mm','4.4 mm balanced','Balanced output for compatible gear.'),
          opt('2-pin','0.78 mm 2-pin','Common detachable connector for Kiwi Ears IEMs.'),
          opt('headphone','Headphone connector','Single- or dual-3.5 mm headphone-side connection.'),
          opt('microphone','Microphone support','Calls, inline controls, or gaming chat.')] },
      budgetStep(true), priorityStep('accessory')];
    return [categoryStep,
      { id: 'scenarios', kicker: 'Question 2', title: 'Where will you use it most?',
        help: 'Choose one or two. The finder balances both when ranking products.', type: 'multi', max: 2,
        options: scenarioOptions(cat || 'audio') },
      { id: 'sound', kicker: 'Question 3', title: 'Which sound feels most like you?',
        help: 'Broad listening preferences, not measurements. Skip this if you are still learning your taste.', type: 'sound',
        options: [
          opt('balanced','Balanced','Even, natural, and versatile.'),
          opt('powerful','Powerful','Deep bass, impact, and physical scale.'),
          opt('vocal','Vocal','Voices sit closer and clearer.'),
          opt('warm','Warm','Fuller body with a smoother top end.'),
          opt('spacious','Spacious','More air, openness, and perceived space.')] },
      budgetStep(false), priorityStep(cat || 'audio')];
  }
  function budgetStep(accessory) {
    return { id: 'budget', kicker: 'Question 4', title: 'What is your comfortable budget?',
      help: 'A guide, not a hard wall — slightly higher options may appear when they match especially well.', type: 'single',
      options: accessory ? [
        opt('under-25','Under $25','Simple adapters, tips, and compact essentials.'),
        opt('under-50','Under $50','More connection and cable options.'),
        opt('under-100','Under $100','The complete accessory range.'),
        opt('flexible','Flexible budget','Prioritize the best functional fit.')] : [
        opt('under-50','Under $50','Accessible everyday and entry-level choices.'),
        opt('under-100','Under $100','A wider mix of IEMs and headphones.'),
        opt('under-200','Under $200','Stronger technical and driver options.'),
        opt('under-350','Under $350','Premium hybrids, monitors, and headphones.'),
        opt('350-plus','$350 and above','Flagship and professional-level options.'),
        opt('flexible','Flexible budget','Prioritize the strongest overall fit.')] };
  }
  function priorityStep(cat) {
    return { id: 'priorities', kicker: 'Question 5', title: 'What matters most after the basics?',
      help: 'Choose up to three. This is where closely matched products begin to separate.', type: 'multi', max: 3,
      options: (PRIORITY_GROUPS[cat] || PRIORITY_GROUPS.audio).map(function (k) { return opt(k, LABELS.priority[k], PRIORITY_DESC[k] || ''); }) };
  }

  /* --------------------------------------------------------------- state */
  var state = { step: 0, category: null, scenarios: [], sound: null, budget: null, priorities: [], need: null, connections: [], done: false };
  var compare = [];

  function answerCount() {
    var n = 0;
    if (state.category) n++;
    if (state.category === 'accessory') { if (state.need) n++; if (state.connections.length) n++; }
    else { if (state.scenarios.length) n++; if (state.sound) n++; }
    if (state.budget) n++;
    if (state.priorities.length) n++;
    return n;
  }

  /* URL state — ?fq=cat.scen1+scen2.sound.budget.prio1+prio2 (dot-separated
     slots so an empty slot survives), plus fqs=1 when results are open. */
  function updateURL() {
    try {
      var url = new URL(window.location.href);
      var a = state.category === 'accessory'
        ? [state.category, state.need || '', state.connections.join('+'), state.budget || '', state.priorities.join('+')]
        : [state.category || '', state.scenarios.join('+'), state.sound || '', state.budget || '', state.priorities.join('+')];
      if (a.some(Boolean)) url.searchParams.set('fq', a.join('.'));
      else url.searchParams.delete('fq');
      if (state.done) url.searchParams.set('fqs', '1'); else url.searchParams.delete('fqs');
      history.replaceState({}, '', url);
    } catch (e) {}
  }
  function readURL() {
    try {
      var url = new URL(window.location.href);
      var fq = url.searchParams.get('fq');
      if (!fq) return;
      var a = fq.split('.');
      state.category = a[0] || null;
      if (state.category === 'accessory') {
        state.need = a[1] || null;
        state.connections = a[2] ? a[2].split('+') : [];
      } else {
        state.scenarios = a[1] ? a[1].split('+') : [];
        state.sound = a[2] || null;
      }
      state.budget = a[3] || null;
      state.priorities = a[4] ? a[4].split('+') : [];
      state.done = url.searchParams.get('fqs') === '1';
      state.step = state.done ? steps().length - 1 : 0;
    } catch (e) {}
  }

  /* ------------------------------------------------------------- scoring */
  function eligible(p) {
    if (!state.category) return p.cat !== 'accessory';
    if (state.category === 'audio') return p.cat !== 'accessory';
    return p.cat === state.category;
  }
  function budgetPoints(p) {
    var code = state.budget;
    if (!code || code === 'flexible') return { raw: 0, max: 0, matched: false };
    var range = BUDGETS[code];
    if (!range) return { raw: 0, max: 0, matched: false };
    if (p.price == null || isNaN(p.price)) return { raw: 7, max: 20, matched: false };
    if (p.price >= range[0] && p.price <= range[1]) return { raw: 20, max: 20, matched: true };
    if (p.price < range[0]) return { raw: 14, max: 20, matched: true };
    if (isFinite(range[1]) && p.price <= range[1] * 1.18) return { raw: 9, max: 20, matched: false };
    return { raw: 0, max: 20, matched: false };
  }
  function matchesPriority(p, key) {
    var tags = PRIORITY_MATCH[key] || [key];
    var hay = {};
    [].concat(p.prio, p.sounds, p.scen, p.conn).forEach(function (t) { hay[t] = 1; });
    return tags.some(function (t) { return hay[t]; });
  }
  function score(p) {
    var raw = 0, max = 0, reasons = [];
    if (state.category) {
      max += 30;
      var catMatch = state.category === 'audio' ? p.cat !== 'accessory' : p.cat === state.category;
      if (catMatch) { raw += 30; reasons.push(p.sub + ' matches the product type you selected.'); }
    }
    if (state.need && p.cat === 'accessory') {
      max += 30;
      if (p.need === state.need) { raw += 30; reasons.push('Designed for ' + LABELS.need[state.need].toLowerCase() + '.'); }
    }
    if (state.connections.length && p.cat === 'accessory') {
      state.connections.forEach(function (c) {
        max += 11;
        if (p.conn.indexOf(c) > -1) { raw += 11; reasons.push('Supports ' + LABELS.connection[c] + '.'); }
      });
    }
    state.scenarios.forEach(function (sc) {
      max += 18;
      if (p.scen.indexOf(sc) > -1) { raw += 18; reasons.push('Strong fit for ' + LABELS.scenario[sc].toLowerCase() + '.'); }
    });
    if (state.sound && p.cat !== 'accessory') {
      max += 26;
      var i = p.sounds.indexOf(state.sound);
      if (i === 0) { raw += 26; reasons.push(LABELS.sound[state.sound] + ' is its primary sound direction.'); }
      else if (i > 0) { raw += 18; reasons.push('Its tuning also supports a ' + LABELS.sound[state.sound].toLowerCase() + ' preference.'); }
    }
    var b = budgetPoints(p);
    raw += b.raw; max += b.max;
    if (b.matched && state.budget && state.budget !== 'flexible') reasons.push('Fits the ' + LABELS.budget[state.budget].toLowerCase() + ' budget.');
    state.priorities.forEach(function (k) {
      max += 12;
      if (matchesPriority(p, k)) { raw += 12; reasons.push('Supports your priority: ' + LABELS.priority[k].toLowerCase() + '.'); }
    });
    max += 4;
    if (p.avail === 'available') raw += 4; else if (p.avail === 'preorder') raw += 2;
    var fit = max ? raw / max : 0;
    var confidence = answerCount() / steps().length;
    var display = max ? clamp(Math.round(50 + 48 * fit * (0.72 + 0.28 * confidence)), 52, 98) : 0;
    var seen = {}, uniq = [];
    reasons.forEach(function (r) { if (!seen[r]) { seen[r] = 1; uniq.push(r); } });
    return { fit: fit, display: display, reasons: uniq.slice(0, 4) };
  }
  function featuredRank(p) { var i = FEATURED.indexOf(p.handle); return i < 0 ? 999 : i; }
  function ranked() {
    var list = PRODUCTS.filter(eligible);
    if (state.category === 'accessory' && state.need) {
      var exact = list.filter(function (p) { return p.need === state.need; });
      if (exact.length) list = exact;
    }
    return list.map(function (p) { return { p: p, s: score(p) }; }).sort(function (a, b) {
      var d = b.s.fit - a.s.fit;
      if (Math.abs(d) > 0.001) return d;
      var order = { available: 0, preorder: 1, soldout: 2 };
      if (order[a.p.avail] !== order[b.p.avail]) return order[a.p.avail] - order[b.p.avail];
      var f = featuredRank(a.p) - featuredRank(b.p);
      if (f) return f;
      return (a.p.price || 9999) - (b.p.price || 9999);
    });
  }

  /* ------------------------------------------------------------- render */
  var els = {
    question: $('[data-fndq-question]'), stepnum: $('[data-fndq-stepnum]'), steptotal: $('[data-fndq-steptotal]'),
    dots: $('[data-fndq-dots]'), back: $('[data-fndq-back]'), next: $('[data-fndq-next]'), skip: $('[data-fndq-skip]'),
    live: $('[data-fndq-live]'), confidence: $('[data-fndq-confidence]'), early: $('[data-fndq-results-early]'),
    results: $('#fndq-results'), guided: $('#fndq-guided'), profile: $('[data-fndq-profile]'),
    topcard: $('[data-fndq-topcard]'), topscore: $('[data-fndq-topscore]'), reasons: $('[data-fndq-reasons]'),
    topvendor: $('[data-fndq-topvendor]'), topname: $('[data-fndq-topname]'), topsub: $('[data-fndq-topsub]'),
    topprice: $('[data-fndq-topprice]'), topcmp: $('[data-fndq-topcmp]'), toprate: $('[data-fndq-toprate]'),
    topratev: $('[data-fndq-topratev]'), topatc: $('[data-fndq-topatc]'), topwish: $('[data-fndq-topwish]'),
    tradeoff: $('[data-fndq-tradeoff]'), alts: $('[data-fndq-alts]'), pool: $('[data-fndq-pool]'),
    cbar: $('[data-fndq-cbar]'), ccount: $('[data-fndq-ccount]'), copen: $('[data-fndq-copen]'),
    cclear: $('[data-fndq-cclear]'), cdialog: $('[data-fndq-cdialog]'), ctable: $('[data-fndq-ctable]'), cclose: $('[data-fndq-cclose]')
  };

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function answerFor(id) {
    return { category: state.category, scenarios: state.scenarios, sound: state.sound, budget: state.budget, priorities: state.priorities, need: state.need, connections: state.connections }[id];
  }
  function setAnswer(step, value) {
    if (step.type === 'multi') {
      var cur = answerFor(step.id).slice();
      var i = cur.indexOf(value);
      if (i > -1) cur.splice(i, 1);
      else if (cur.length < (step.max || 9)) cur.push(value);
      else return;   /* at the cap: the counter under the title says so */
      state[step.id] = cur;
    } else {
      var changed = state[step.id] !== value;
      state[step.id] = value;
      /* category change resets the branch-specific answers — the options they
         referred to may no longer exist */
      if (step.id === 'category' && changed) {
        state.scenarios = []; state.sound = null; state.need = null;
        state.connections = []; state.priorities = [];
      }
    }
    render();
  }

  function renderQuestion() {
    var all = steps();
    state.step = clamp(state.step, 0, all.length - 1);
    var step = all[state.step];
    els.stepnum.textContent = step.kicker;
    els.steptotal.textContent = all.length;
    els.dots.textContent = '';
    all.forEach(function (_, i) {
      var d = el('span', 'fndq-dot' + (i === state.step ? ' is-here' : i < state.step ? ' is-done' : ''));
      els.dots.appendChild(d);
    });
    els.back.hidden = state.step === 0;
    els.skip.hidden = !!step.required;
    els.next.textContent = state.step === all.length - 1 ? 'See my matches' : 'Next';

    var q = els.question;
    q.textContent = '';
    var h = el('h3', 'm-0 mb-2 font-display text-[24px] leading-[1.15] tracking-[-0.04em] text-ink wght-700', step.title);
    h.setAttribute('tabindex', '-1');
    q.appendChild(h);
    q.appendChild(el('p', 'm-0 mb-6 font-sans text-[14px] leading-[1.55] text-ink-2/70 wght-400', step.help));
    if (step.type === 'multi') {
      var a = answerFor(step.id);
      q.appendChild(el('p', 'm-0 mb-3 font-mono text-[10px] uppercase tracking-[0.14em] text-kiwi-ink wght-700', a.length + ' of ' + step.max + ' selected'));
    }
    var grid = el('div', 'grid grid-cols-2 gap-3 max-sm:grid-cols-1');
    step.options.forEach(function (o) {
      var on = step.type === 'multi' ? answerFor(step.id).indexOf(o.value) > -1 : answerFor(step.id) === o.value;
      var b = el('button', 'fndq-opt' + (on ? ' is-on' : ''));
      b.type = 'button';
      b.setAttribute('aria-pressed', String(on));
      if (step.type === 'sound' && SOUND_PATHS[o.value]) {
        var svgNS = 'http://www.w3.org/2000/svg';
        var svg = document.createElementNS(svgNS, 'svg');
        svg.setAttribute('viewBox', '0 0 220 58');
        svg.setAttribute('class', 'fndq-optgraph');
        svg.setAttribute('aria-hidden', 'true');
        var glow = document.createElementNS(svgNS, 'path');
        glow.setAttribute('d', SOUND_PATHS[o.value]); glow.setAttribute('class', 'fndq-gglow');
        var line = document.createElementNS(svgNS, 'path');
        line.setAttribute('d', SOUND_PATHS[o.value]); line.setAttribute('class', 'fndq-gline');
        svg.appendChild(glow); svg.appendChild(line);
        b.appendChild(svg);
      }
      b.appendChild(el('strong', 'fndq-optlabel', o.label));
      b.appendChild(el('span', 'fndq-optdesc', o.desc));
      b.addEventListener('click', function () { setAnswer(step, o.value); });
      grid.appendChild(b);
    });
    q.appendChild(grid);
  }

  function renderLive() {
    var list;
    if (!state.category) {
      list = PRODUCTS.filter(function (p) { return p.cat !== 'accessory'; })
        .sort(function (a, b) { return featuredRank(a) - featuredRank(b); })
        .slice(0, 3).map(function (p, i) { return { p: p, s: { display: 82 - i * 3 } }; });
    } else list = ranked().slice(0, 3);
    els.live.textContent = '';
    list.forEach(function (row, i) {
      var card = el('div', 'fndq-lm');
      var img = el('img', 'fndq-lmimg');
      img.src = row.p.img; img.alt = ''; img.loading = 'lazy'; img.width = 64; img.height = 64;
      card.appendChild(img);
      var copy = el('span', 'min-w-0 flex-1');
      copy.appendChild(el('small', 'fndq-lmkick', i === 0 ? 'Leading match' : 'Alternative ' + i));
      copy.appendChild(el('strong', 'fndq-lmname', row.p.title));
      copy.appendChild(el('span', 'fndq-lmtags', (row.p.tags.slice(0, 3).join(' · ') || row.p.sub)));
      card.appendChild(copy);
      var meter = el('span', 'fndq-meter', (row.s.display || 78) + '%');
      meter.title = 'Finder match based on selected preferences';
      card.appendChild(meter);
      els.live.appendChild(card);
    });
    var c = answerCount() / steps().length;
    els.confidence.textContent = c >= 0.8 ? 'High confidence' : c >= 0.4 ? 'Building confidence' : 'Early guess';
    els.confidence.className = 'fndq-conf' + (c >= 0.8 ? ' is-high' : c >= 0.4 ? ' is-mid' : '');
    els.early.hidden = answerCount() < 2 || state.done;
  }

  /* Cards move between the pool, the top slot and the alternatives grid —
     returned home before every re-render so a card can never be in two
     places, and the pool keeps live prices without any refetching. */
  function returnCardsHome() {
    [].concat($$('[data-fndq-item]', els.topcard), $$('[data-fndq-item]', els.alts)).forEach(function (n) {
      els.pool.appendChild(n);
    });
  }

  function renderResults() {
    var rows = ranked();
    if (!rows.length) return;
    returnCardsHome();
    var top = rows[0];
    els.topcard.appendChild(top.p.node);
    els.topscore.textContent = top.s.display + '% match';

    /* the buy box — server skeleton, filled here from the pool item's facts */
    var tp = top.p;
    els.topvendor.textContent = tp.vendor || 'Kiwi Ears';
    els.topname.textContent = tp.name;
    els.topname.href = tp.url;
    els.topsub.textContent = tp.blurb || '';
    els.topsub.hidden = !tp.blurb;
    els.topprice.textContent = (tp.varies && !tp.cmpFmt ? 'From ' : '') + (tp.priceFmt || '');
    els.topcmp.textContent = tp.cmpFmt || '';
    els.topcmp.hidden = !tp.cmpFmt;
    if (tp.rating) {
      els.topratev.textContent = tp.rating + (tp.rcount ? ' \u00b7 ' + tp.rcount + ' review' + (tp.rcount === '1' ? '' : 's') : '');
      els.toprate.hidden = false;
    } else {
      els.toprate.hidden = true;
    }
    els.topatc.dataset.vid = tp.vid || '';
    els.topatc.disabled = tp.avail === 'soldout' || !tp.vid;
    els.topatc.textContent = tp.avail === 'soldout' ? 'Sold out' : tp.avail === 'preorder' ? 'Pre-order' : 'Add to Cart';
    /* the heart is kiwi-wishlist.js's own [data-wishlist-toggle] contract —
       only the target handle and the initial pressed state are ours to set */
    els.topwish.setAttribute('data-handle', tp.handle);
    var wOn = !!(window.KiwiWishlist && window.KiwiWishlist.has(tp.handle));
    els.topwish.setAttribute('aria-pressed', String(wOn));
    els.topwish.setAttribute('aria-label', wOn ? 'Remove from wishlist' : 'Add to wishlist');

    els.reasons.textContent = '';
    (top.s.reasons.length ? top.s.reasons : ['A strong all-round fit for the answers you gave.']).forEach(function (r) {
      var li = el('li', 'fndq-reason', r);
      els.reasons.appendChild(li);
    });
    els.tradeoff.textContent = top.p.tradeoff;
    els.alts.textContent = '';
    rows.slice(1, 5).forEach(function (row) {
      var wrap = el('div', 'fndq-alt flex min-w-0 flex-col gap-3');
      var head = el('div', 'flex items-center justify-between gap-3');
      head.appendChild(el('span', 'fndq-meter', row.s.display + '%'));
      var why = el('span', 'font-sans text-[12px] leading-[1.4] text-ink-2/70 wght-500', row.s.reasons[0] || '');
      head.appendChild(why);
      wrap.appendChild(head);
      wrap.appendChild(row.p.node);
      var t = el('p', 'm-0 font-sans text-[12px] leading-[1.5] text-ink-2/65 wght-400', row.p.tradeoff);
      wrap.appendChild(t);
      var cbtn = el('button', 'fndq-ghostbtn self-start', compare.indexOf(row.p.handle) > -1 ? 'In comparison' : 'Compare');
      cbtn.type = 'button';
      cbtn.addEventListener('click', function () { toggleCompare(row.p.handle); });
      wrap.appendChild(cbtn);
      els.alts.appendChild(wrap);
    });
    var chips = [];
    if (state.category) chips.push(LABELS.category[state.category]);
    if (state.need) chips.push(LABELS.need[state.need]);
    state.connections.forEach(function (c) { chips.push(LABELS.connection[c]); });
    state.scenarios.forEach(function (sc) { chips.push(LABELS.scenario[sc]); });
    if (state.sound) chips.push(LABELS.sound[state.sound] + ' sound');
    if (state.budget) chips.push(LABELS.budget[state.budget]);
    state.priorities.forEach(function (k) { chips.push(LABELS.priority[k]); });
    els.profile.textContent = '';
    chips.forEach(function (c) { els.profile.appendChild(el('span', 'fndq-chip', c)); });
  }

  function showResults() {
    state.done = true;
    renderResults();
    els.results.classList.remove('hidden');
    updateURL();
    els.results.focus({ preventScroll: false });
    els.results.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
  }

  /* ------------------------------------------------------------ compare */
  function toggleCompare(handle) {
    var i = compare.indexOf(handle);
    if (i > -1) compare.splice(i, 1);
    else {
      if (compare.length >= 3) return;   /* handoff rule 6: never more than three */
      compare.push(handle);
    }
    els.ccount.textContent = compare.length;
    els.cbar.hidden = compare.length === 0;
    if (state.done) renderResults();
  }
  function renderCompare() {
    /* The PDP comparison anatomy (sections/pk-compare, Figma 221:918), not a
       generic <table>: a grid with a 180px label rail, product header cells
       carrying a 150px framed image + name, hairline rows, and the FIRST
       column washed in the kiwi highlight — here that is the first product
       added, usually the leading match. Same classes live in main.css because
       the JIT cannot see class names written in JS. */
    var picks = compare.map(function (h) { return PRODUCTS.filter(function (p) { return p.handle === h; })[0]; }).filter(Boolean);
    var rows = [
      ['Sound', function (p) { return p.sounds.map(function (x) { return LABELS.sound[x]; }).join(', ') || '—'; }],
      ['Best for', function (p) { return p.scen.map(function (x) { return LABELS.scenario[x] || x; }).slice(0, 3).join(', ') || '—'; }],
      ['Standout', function (p) { return p.strength; }],
      ['Worth knowing', function (p) { return p.tradeoff; }],
      ['Availability', function (p) { return p.avail === 'available' ? 'Available' : p.avail === 'preorder' ? 'Pre-order' : 'Sold out'; }],
      ['Price', function (p) { return p.price != null && !isNaN(p.price) ? '$' + p.price.toFixed(2) : '—'; }]
    ];
    var grid = el('div', 'fndq-cmpgrid');
    grid.style.gridTemplateColumns = '150px repeat(' + picks.length + ', minmax(190px, 1fr))';

    /* header band: empty label cell, then one product cell per pick */
    grid.appendChild(el('div', 'fndq-cmpspacer'));
    picks.forEach(function (p, i) {
      var cell = el('a', 'fndq-cmpprod' + (i === 0 ? ' is-lead' : ''));
      cell.href = p.url;
      var frame = el('span', 'fndq-cmpimg');
      var img = el('img'); img.src = p.img; img.alt = p.title; img.loading = 'lazy'; img.width = 300; img.height = 300;
      frame.appendChild(img);
      cell.appendChild(frame);
      var copy = el('span', 'fndq-cmpcopy');
      copy.appendChild(el('span', 'fndq-cmpsub', p.sub));
      copy.appendChild(el('span', 'fndq-cmpname', p.title.replace(/^Kiwi Ears\s*/i, '')));
      cell.appendChild(copy);
      grid.appendChild(cell);
    });

    rows.forEach(function (r) {
      grid.appendChild(el('p', 'fndq-cmplbl', r[0]));
      picks.forEach(function (p, i) {
        grid.appendChild(el('p', 'fndq-cmpval' + (i === 0 ? ' is-lead' : ''), r[1](p)));
      });
    });
    els.ctable.textContent = '';
    els.ctable.appendChild(grid);
  }

  /* -------------------------------------------------------------- wire */
  els.next.addEventListener('click', function () {
    var all = steps();
    var step = all[state.step];
    var a = answerFor(step.id);
    if (step.required && !(Array.isArray(a) ? a.length : a)) {
      els.question.querySelector('h3').focus();
      return;
    }
    if (state.step >= all.length - 1) { showResults(); return; }
    state.step++;
    render();
    els.question.querySelector('h3').focus();
  });
  els.back.addEventListener('click', function () { if (state.step > 0) { state.step--; render(); } });
  els.skip.addEventListener('click', function () {
    var step = steps()[state.step];
    state[step.id] = step.type === 'multi' ? [] : null;
    if (state.step >= steps().length - 1) { showResults(); return; }
    state.step++;
    render();
  });
  els.early.addEventListener('click', showResults);
  $('[data-fndq-restart]').addEventListener('click', function () {
    state = { step: 0, category: null, scenarios: [], sound: null, budget: null, priorities: [], need: null, connections: [], done: false };
    compare = []; els.cbar.hidden = true; els.ccount.textContent = '0';
    returnCardsHome();
    els.results.classList.add('hidden');
    render();
    els.guided.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
  });
  $('[data-fndq-share]').addEventListener('click', function (e) {
    var b = e.currentTarget, was = b.textContent;
    (navigator.clipboard ? navigator.clipboard.writeText(window.location.href) : Promise.reject())
      .then(function () { b.textContent = 'Link copied'; })
      .catch(function () { b.textContent = window.location.href.length > 40 ? 'Copy the address bar URL' : was; })
      .then(function () { setTimeout(function () { b.textContent = was; }, 1800); });
  });
  $('[data-fndq-compare-top]').addEventListener('click', function () {
    var rows = ranked();
    if (rows.length) toggleCompare(rows[0].p.handle);
  });
  els.copen.addEventListener('click', function () {
    renderCompare();
    els.cdialog.showModal();
    /* The page behind a modal must not scroll. Two engines to silence:
       Lenis (stop(); the dialog also carries data-lenis-prevent) and native
       scrolling (overflow hidden on the root, since ::backdrop does not trap
       wheel events on its own). Both restored in the close handler below,
       which fires for the Close button, Esc, AND the backdrop click, because
       dialog.close() always emits 'close'. */
    if (window.lenis && window.lenis.stop) window.lenis.stop();
    document.documentElement.style.overflow = 'hidden';
  });
  els.cdialog.addEventListener('close', function () {
    if (window.lenis && window.lenis.start) window.lenis.start();
    document.documentElement.style.overflow = '';
  });
  els.cclose.addEventListener('click', function () { els.cdialog.close(); });
  els.cclear.addEventListener('click', function () { compare = []; els.cbar.hidden = true; els.ccount.textContent = '0'; if (state.done) renderResults(); });
  els.cdialog.addEventListener('click', function (e) { if (e.target === els.cdialog) els.cdialog.close(); });

  function render() {
    renderQuestion();
    renderLive();
    updateURL();
  }

  readURL();
  render();
  if (state.done) showResults();
})();
